import { inject, Injectable } from '@angular/core';
import {
    FiOnboardingStepNotificationRecipientType,
    FiOnboardingStepNotificationReminderTime,
} from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { IOnboardingStepNotification } from '../../fi-onboarding';
import { isDefined } from '@fi-app/shared/utils';
import { FiOnboardingNotificationEditorDialogComponent } from './notification-editor-dialog/notification-editor-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-app/shared/app-text';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';

@Injectable()
export class FiOnboardingAutomatedNotificationHelperService {
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);

    readonly ParticipantsReminderTimeOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiOnboardingStepNotificationReminderTime.OneDay,
            Name: this.appTextService.get('automated_notification.step_no_answer_one_day'),
        },
        {
            Id: FiOnboardingStepNotificationReminderTime.ThreeDays,
            Name: this.appTextService.get('automated_notification.step_no_answer_three_days'),
        },
        {
            Id: FiOnboardingStepNotificationReminderTime.SevenDays,
            Name: this.appTextService.get('automated_notification.step_no_answer_seven_days'),
        },
    ];

    readonly ReminderTimeOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiOnboardingStepNotificationReminderTime.Completed,
            Name: this.appTextService.get('automated_notification.step_completed'),
        },
        ...this.ParticipantsReminderTimeOptions,
    ];

    readonly RecipientTypeOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiOnboardingStepNotificationRecipientType.RolesLocal,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_local_roles'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.Roles,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_roles'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.Users,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_staff_members'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.EmailAddress,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_email_address'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.SMS,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_sms'),
        },
    ];

    readonly RecipientTypeHighlightedOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiOnboardingStepNotificationRecipientType.RolesLocal,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_roles_highlighted'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.Roles,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_roles_highlighted'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.Users,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_staff_members_highlighted'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.EmailAddress,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_email_address_highlighted'),
        },
        {
            Id: FiOnboardingStepNotificationRecipientType.SMS,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_sms_highlighted'),
        },
    ];

    private readonly directNotificationMethod = [
        FiOnboardingStepNotificationRecipientType.EmailAddress,
        FiOnboardingStepNotificationRecipientType.SMS,
    ];
    isNotificationMethodHidden(recipientType: FiOnboardingStepNotificationRecipientType): boolean {
        return this.directNotificationMethod.includes(recipientType);
    }

    openNotificationDialog(
        notification: IOnboardingStepNotification,
        isParticipants: boolean,
    ): IFiDialogResult<IOnboardingStepNotification> {
        if (!isDefined(notification)) {
            notification = this.getNewNotificationInstance(isParticipants);
        }

        const headerTextId = isParticipants
            ? 'automated_notification.send_to_participants'
            : 'automated_notification.send_to_others';

        const reminderTimeOptions = isParticipants ? this.ParticipantsReminderTimeOptions : this.ReminderTimeOptions;

        return this.fiDialogsService.custom({
            headerText: this.appTextService.get(headerTextId),
            contentComponentRef: FiOnboardingNotificationEditorDialogComponent,
            contentComponentData: { notification, reminderTimeOptions },
            hideDialogTopBorder: true,
            headerDividerClass: 'BrandedBorderColor',
            isHeaderDividerShown: true,
            yesButtonText: this.appTextService.get('done'),
            noButtonText: this.appTextService.get('common.cancel'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-dialog-content-no-bottom-padding',
                    'fi-automated-notification-dialog-panel',
                ],
                maxWidth: '850px',
                width: '90%',
                autoFocus: false,
            },
        });
    }

    private getNewNotificationInstance(isParticipants: boolean): IOnboardingStepNotification {
        const RecipientType = isParticipants
            ? FiOnboardingStepNotificationRecipientType.Participants
            : FiOnboardingStepNotificationRecipientType.RolesLocal;

        return {
            RecipientType,
            ReminderTime: FiOnboardingStepNotificationReminderTime.ThreeDays,
            ViaSMS: false,
            ViaEmail: false,
            ViaNotification: true,
            UserIds: [],
            RoleIds: [],
            UnitIds: [],
            UserContacts: [],
        };
    }

    getParticipantsNotification(notifications: IOnboardingStepNotification[]): IOnboardingStepNotification[] {
        return notifications.filter(
            (notif) => notif.RecipientType === FiOnboardingStepNotificationRecipientType.Participants,
        );
    }

    getNonParticipantsNotification(notifications: IOnboardingStepNotification[]): IOnboardingStepNotification[] {
        return notifications.filter(
            (notif) => notif.RecipientType !== FiOnboardingStepNotificationRecipientType.Participants,
        );
    }
}
